import { RecentlyViewed } from "@/components/RecentlyViewed";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { jmd } from "@/lib/format";
import { Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { WeightNote, FlatbedAlert, QtyStepper } from "@/components/QtyStepper";
import { fmtKg } from "@/lib/weight";

export default function Cart() {
  const { items, setQty, removeItem, subtotal } = useCart();
  const totalKg = items.reduce((s, i) => s + (i.unit_weight_kg ? i.unit_weight_kg * i.quantity : 0), 0);
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <ShoppingBag size={56} className="mx-auto text-slate-300 mb-4" />
          <h1 className="font-display text-2xl font-bold">Your cart is empty</h1>
          <p className="text-muted-foreground mt-2">Discover thousands of products, hotels and Jamaica Made brands.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Start Shopping <ArrowRight size={18} /></Link>
        </div>
        <RecentlyViewed />
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-bold tracking-tight mb-6">Shopping Cart</h1>
      {totalKg > 0 && <div className="mb-4 text-sm" data-testid="cart-total-weight">Total weight of weighed items: <b>{fmtKg(totalKg)}</b>{items.some((i) => i.unit_weight_kg == null) && <span className="text-muted-foreground"> · other items have no listed weight</span>}<FlatbedAlert kg={totalKg} desc="Shopping cart bulk load" /></div>}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-4 bg-card rounded-2xl border border-border p-4">
              <img src={i.image} alt={i.name} className="h-24 w-24 rounded-xl object-cover bg-slate-100" />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${i.product_id}`} className="font-display font-semibold hover:text-emerald line-clamp-2">{i.name}</Link>
                <div className="font-display font-bold text-lg mt-1">{jmd(i.price)}</div>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <QtyStepper size="sm" id={`cart-qty-${i.product_id}`} value={i.quantity} onChange={(v) => setQty(i.product_id, v)} unit={i.unit} />
                  <WeightNote unitKg={i.unit_weight_kg} qty={i.quantity} unit={i.unit} id={`cart-weight-${i.product_id}`} compact />
                  <button onClick={() => removeItem(i.product_id)} className="text-coral flex items-center gap-1 text-sm"><Trash2 size={16} /> Remove</button>
                </div>
              </div>
              <div className="font-display font-bold text-right whitespace-nowrap shrink-0">{jmd(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>
        <div className="bg-card rounded-2xl border border-border p-6 h-fit sticky top-28">
          <h3 className="font-display font-bold text-lg mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold">{jmd(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="text-muted-foreground">Calculated at checkout</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Includes GCT (15%)</span><span className="font-semibold">{jmd(Math.round(subtotal * 15 / 115))}</span></div>
          </div>
          <div className="border-t border-border mt-4 pt-4 flex justify-between font-display font-bold text-lg">
            <span>Estimated Total</span><span>{jmd(Math.round(subtotal))}</span>
          </div>
          <button onClick={() => navigate("/checkout")} className="w-full mt-5 bg-ink text-white rounded-xl py-3.5 font-semibold hover:bg-emerald-deep transition-colors" data-testid="cart-checkout-btn">
            Proceed to Checkout
          </button>
          <p className="text-xs text-muted-foreground text-center mt-3">Guest checkout available · No account needed</p>
        </div>
      </div>
      <RecentlyViewed />
    </div>
  );
}
